import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import KineticText from '../KineticText/KineticText';
import robotGatewayMascot from '../../assets/feature-slides/robot-gateway-mascot.png';
import robotSearchMascot from '../../assets/feature-slides/robot-search-mascot.png';
import robotAgentsMascot from '../../assets/feature-slides/robot-agents-mascot.png';
import robotSkillsMascot from '../../assets/feature-slides/robot-skills-mascot.png';
import robotAuthMascot from '../../assets/feature-slides/robot-auth-mascot.png';
import robotCustomEntitiesMascot from '../../assets/feature-slides/robot-custom-entities-mascot.png';
import robotVaultMascot from '../../assets/feature-slides/robot-vault-mascot.png';

// Original interactive feature explorer for the landing page -- no reference
// screenshots or mockup PNGs involved. Content is grounded in the platform's
// own README ("Today it serves these core functions") rather than invented
// marketing copy. Icons are simple line-art authored for this component.
const ACCENT = '#8b5cf6';
const AUTO_ADVANCE_MS = 6500;

interface Feature {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  highlights: string[];
  icon: React.ReactNode;
  /** Not actively being worked on right now -- shown dimmed with a "Coming soon" badge. */
  comingSoon?: boolean;
}

const FEATURES: Feature[] = [
  {
    id: 'gateway',
    eyebrow: 'Unified MCP Gateway',
    title: 'One gateway for every MCP server',
    description:
      'One centralized access point for every MCP server your teams use.',
    highlights: [
      'One connection point for VS Code, Cursor, Claude Code, and other clients',
      'Central IT manages the infrastructure instead of every developer managing their own',
      'No more scattered API keys and ad hoc server configs'
    ],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="10" width="16" height="9" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M7.5 10V7a4.5 4.5 0 019 0v3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="14.5" r="1.4" fill="currentColor" />
      </svg>
    )
  },
  {
    id: 'registry',
    eyebrow: 'MCP Servers Registry',
    title: 'Register, discover, and govern every server',
    description:
      'Register servers once and make them discoverable, with unified governance built in.',
    highlights: [
      'Semantic and lexical search across every registered server',
      'Schema-driven registration, registry cards, and voting',
      'One access-control and audit model for the whole catalog'
    ],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M20 20l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  {
    id: 'skills',
    eyebrow: 'Skills Registry',
    title: 'Reusable skills, versioned and scanned',
    description:
      'Register, version, and discover reusable AI skills, scanned for security before they go live.',
    highlights: [
      'Version history for every registered skill',
      'Automatic security scanning before a skill is marked trusted',
      'Discover and reuse skills other teams have already built'
    ],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 6l-5 6 5 6M16 6l5 6-5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    id: 'auth',
    eyebrow: 'Per-user Authentication',
    title: "Per-user identity where it matters, shared credentials where it doesn't",
    description:
      'Per-user OAuth where servers support it, shared API keys or tokens where they don\'t -- your choice, per server.',
    highlights: [
      'OAuth 2LO/3LO flows for servers that support per-user identity',
      'Shared API key, bearer token, or caller token auth for the rest',
      'Scoped permissions checked per call, per user, per server'
    ],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  {
    id: 'custom-entities',
    eyebrow: 'Custom Entity Registry',
    title: 'Any AI asset, registered under your own schema',
    description:
      'Admin-defined schemas let the registry hold any AI asset -- not just servers, agents, and skills.',
    highlights: [
      'Admin-defined entity types and schemas, no code changes required',
      'Lexical and semantic search, voting, and registry cards for every custom type',
      'Same governance and audit trail as MCP servers, agents, and skills'
    ],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M17 13.5v7M13.5 17h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  {
    id: 'vault',
    eyebrow: 'Credential Vault & Audit Trail',
    title: 'Centralized secrets, complete audit history',
    description:
      'Encrypted credential storage with every registration, grant, and call logged.',
    highlights: [
      "Third-party tokens live in a secrets vault, never on a developer's laptop",
      'Searchable audit log of every registration, grant, and call',
      'One auditable choke point instead of scattered long-lived tokens'
    ],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="10" width="16" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 14v2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  {
    id: 'agents',
    eyebrow: 'Agent Registry & A2A',
    title: 'Agents that can find and talk to each other',
    description:
      'Agents discover each other through the registry, then talk directly over the open A2A protocol.',
    highlights: [
      'Agents discover other agents regardless of how each one is implemented',
      'Direct, low-latency A2A communication once discovery happens',
      'Rich agent cards describe capabilities, skills, and auth schemes'
    ],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="7.5" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M2.5 19.5c0-2.8 2.3-5 5-5s5 2.2 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M12.5 15c2.4.3 4 2.2 4 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    comingSoon: true
  }
];

interface FeatureExplorerProps {
  onSignInClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const MASCOTS_BY_FEATURE_ID: Record<string, string> = {
  gateway: robotGatewayMascot,
  registry: robotSearchMascot,
  agents: robotAgentsMascot,
  skills: robotSkillsMascot,
  auth: robotAuthMascot,
  'custom-entities': robotCustomEntitiesMascot,
  vault: robotVaultMascot
};

const FeatureExplorer: React.FC<FeatureExplorerProps> = ({ onSignInClick }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The detail panel remounts on every tab switch (key={feature.id} below),
  // which creates a brand-new <img> each time -- the first switch to a given
  // tab would otherwise pay a full image load/decode before it appears.
  // Preloading all 6 up front once, on mount, means every tab switch after
  // that just reuses the browser's already-decoded copy.
  useEffect(() => {
    Object.values(MASCOTS_BY_FEATURE_ID).forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const select = useCallback((index: number) => {
    setActive(((index % FEATURES.length) + FEATURES.length) % FEATURES.length);
    setProgressKey(k => k + 1);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setTimeout(() => {
      // Auto-advance skips "coming soon" tabs (still reachable by clicking
      // or arrow keys) so the rotation doesn't land on an unfinished feature
      // while no one's watching.
      let next = (active + 1) % FEATURES.length;
      while (FEATURES[next].comingSoon && next !== active) {
        next = (next + 1) % FEATURES.length;
      }
      select(next);
    }, AUTO_ADVANCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [active, paused, select]);

  const onTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      select(index + 1);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      select(index - 1);
    }
  };

  const feature = FEATURES[active];

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-8 lg:gap-14 items-stretch"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* tab list */}
      <div role="tablist" aria-label="Platform features" aria-orientation="vertical" className="flex flex-col gap-1.5">
        {FEATURES.map((f, i) => {
          const isActive = i === active;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`feature-panel-${f.id}`}
              id={`feature-tab-${f.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => select(i)}
              onKeyDown={event => onTabKeyDown(event, i)}
              className="relative flex items-center gap-3.5 text-left rounded-xl px-4 py-3.5 transition-colors overflow-hidden"
              style={{
                background: isActive ? 'rgba(139,92,246,0.1)' : 'transparent',
                color: isActive ? '#f3f1fb' : '#9892ad',
                opacity: f.comingSoon && !isActive ? 0.45 : 1,
                filter: f.comingSoon && !isActive ? 'grayscale(0.6) blur(0.3px)' : undefined
              }}
            >
              <span
                className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r"
                style={{ background: isActive ? `linear-gradient(180deg,${ACCENT},#c4b5fd)` : 'transparent' }}
              />
              {isActive && !paused && (
                <span
                  key={progressKey}
                  className="fe-progress absolute left-0 bottom-0 h-[2px] rounded-r"
                  style={{ background: `linear-gradient(90deg,${ACCENT},#c4b5fd)` }}
                />
              )}
              <span
                className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                style={{ background: isActive ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.05)', color: isActive ? '#c4b5fd' : '#8a84a0' }}
              >
                {f.icon}
              </span>
              <span className="text-[13.5px] font-semibold leading-snug">{f.eyebrow}</span>
              {f.comingSoon && (
                <span
                  className="ml-auto text-[9.5px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ color: '#9892ad', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
                >
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* detail panel */}
      <div
        key={feature.id}
        role="tabpanel"
        id={`feature-panel-${feature.id}`}
        aria-labelledby={`feature-tab-${feature.id}`}
        className="fe-panel-enter relative rounded-2xl p-8 lg:p-10 flex flex-col justify-start"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', minHeight: '480px' }}
      >
        <div
          className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full pointer-events-none"
          style={{ background: `radial-gradient(circle, ${ACCENT}26, transparent 70%)`, filter: 'blur(10px)' }}
        />
        <div className="relative flex items-center gap-2.5 mb-5 flex-wrap">
          {MASCOTS_BY_FEATURE_ID[feature.id] && (
            <img
              src={MASCOTS_BY_FEATURE_ID[feature.id]}
              alt=""
              draggable={false}
              className="w-12 h-12 object-contain pointer-events-none select-none"
              style={{ filter: `drop-shadow(0 0 12px ${ACCENT}55)` }}
            />
          )}
          <KineticText
            as="span"
            text={feature.eyebrow}
            className="items-center gap-2 text-[11px] tracking-wide uppercase px-3 py-1 rounded-full w-fit"
            style={{ color: '#c4b5fd', background: 'rgba(139,92,246,0.14)', border: '1px solid rgba(139,92,246,0.32)' }}
          />
          {feature.comingSoon && (
            <span
              className="inline-flex items-center text-[11px] font-semibold tracking-wide uppercase px-3 py-1 rounded-full"
              style={{ color: '#9892ad', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)' }}
            >
              Coming soon
            </span>
          )}
        </div>

        <h3 className="relative m-0 mb-5 font-bold leading-[1.15]" style={{ fontSize: 'clamp(28px,3.2vw,40px)' }}>
          {feature.title}
        </h3>

        <p className="relative m-0 mb-7 leading-relaxed max-w-[620px]" style={{ color: '#b7b2c9', fontSize: '17.5px' }}>
          {feature.description}
        </p>

        <ul className="relative m-0 mb-8 p-0 flex flex-col gap-4" style={{ listStyle: 'none' }}>
          {feature.highlights.map(point => (
            <li key={point} className="flex items-start gap-3 text-base" style={{ color: '#d8d4e6' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="9" stroke="#5eead4" strokeWidth="1.8" />
                <path d="M8 12.5l2.5 2.5L16 9" stroke="#5eead4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {point}
            </li>
          ))}
        </ul>
      </div>

      <style>{`
        @keyframes fe-progress-fill { from { width: 0%; } to { width: 100%; } }
        .fe-progress { animation: fe-progress-fill ${AUTO_ADVANCE_MS}ms linear forwards; }
        @keyframes fe-panel-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .fe-panel-enter { animation: fe-panel-fade .35s ease; }
        @media (prefers-reduced-motion: reduce) {
          .fe-progress { animation: none !important; }
          .fe-panel-enter { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default FeatureExplorer;
